import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { SignInForm } from "@/components/auth/SignInForm";
import { PasswordResetForm } from "@/components/auth/PasswordResetForm";
import { useAuthForm } from "@/hooks/useAuthForm";
import HoneycombBackground from "@/components/ui/honeycomb-background";
import { motion } from "framer-motion";
import { useIsMobile } from "@/hooks/useIsMobile";
import { useAuth } from "@/hooks/useAuth";
import { StripePricingTable } from "@/components/auth/StripePricingTable";
import { cn } from "@/lib/utils";
import { getAppUrl, getCurrentDomainType } from "@/utils/domains";

const Auth = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const defaultTab = searchParams.get("tab") || "signin";
  const isMobile = useIsMobile();
  
  const {
    email,
    setEmail,
    password,
    setPassword,
    loading,
    resetMode,
    setResetMode,
    resetSent,
    resetError,
    handleSignIn,
    handlePasswordReset,
    user,
    from
  } = useAuthForm();

  const { ready } = useAuth();

  useEffect(() => {
    // Only process redirects when user exists AND auth is fully ready
    if (user && ready) {
      console.log("Auth: User authenticated and ready, redirecting to", from);
      
      // Check if we're already on the correct domain
      const currentDomain = getCurrentDomainType();
      const targetUrl = getAppUrl() + from;
      
      console.log("Auth: Current domain type:", currentDomain);
      console.log("Auth: Current hostname:", window.location.hostname);
      console.log("Auth: Target URL:", targetUrl);
      
      if (currentDomain === 'app') {
        // Already on app domain, use normal navigation
        console.log("Auth: Using navigate() for same domain");
        navigate(from, { replace: true });
      } else {
        // On marketing domain, redirect to app domain
        console.log("Auth: Using window.location.href for cross-domain redirect");
        console.log("Auth: About to redirect to:", targetUrl);
        window.location.href = targetUrl;
      }
    }
  }, [user, ready, from, navigate]);

  if (resetMode) {
    return <div className="flex items-center justify-center min-h-screen bg-white">
        <HoneycombBackground className="opacity-10" simplified />
        <div className="w-full max-w-md p-8 relative z-10">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5
        }}>
            <PasswordResetForm email={email} setEmail={setEmail} onSubmit={handlePasswordReset} onBackToSignIn={() => {
            setResetMode(false);
          }} loading={loading} resetSent={resetSent} resetError={resetError} />
          </motion.div>
        </div>
      </div>;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-blue-50/20 relative overflow-hidden">
      {/* Sophisticated Background */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-0 w-[600px] h-[600px] bg-gradient-to-br from-[#FA8C00]/8 via-[#FA8C00]/4 to-transparent rounded-full blur-3xl -translate-x-1/3 -translate-y-1/3" />
        <div className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-gradient-to-tl from-[#4ba3c7]/8 via-[#4ba3c7]/4 to-transparent rounded-full blur-3xl translate-x-1/3 translate-y-1/3" />
      </div>
      
      <div className="flex min-h-screen relative z-10">
        {/* Left side - Enhanced Hero content */}
        <div className="hidden lg:flex flex-1 flex-col justify-center px-12 relative">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, ease: [0.6, -0.05, 0.01, 0.99] }}
            className="max-w-lg"
          >
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="mb-8 flex justify-center"
            >
              <img 
                src="https://imagedelivery.net/ylmXutM6_iJT3CT9AqBdjQ/15521506-bf83-4c94-881f-2ed325767800/public" 
                alt="Hivetivity Logo" 
                className="h-12 w-auto"
              />
            </motion.div>

            <h1 className="text-5xl font-bold text-gray-900 mb-6 tracking-tight">
              <span className="bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 bg-clip-text text-transparent">
                Welcome to Hivetivity
              </span>
            </h1>
            
            <p className="text-xl text-gray-600 mb-10 leading-relaxed font-light">
              The <span className="font-semibold text-gray-800">intelligent platform</span> that brings youth programs to life. Built for modern families and programs that want to stay ahead.
            </p>
            
            <div className="space-y-5 mb-10">
              {[
                { text: "Built for modern families and programs", delay: 0.3 },
                { text: "Stay connected and organized", delay: 0.4 },
                { text: "The future is here", delay: 0.5 }
              ].map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: item.delay }}
                  className="group flex items-center gap-4 p-3 rounded-xl hover:bg-white/70 hover:shadow-md transition-all duration-300"
                >
                  <div className="w-10 h-10 bg-gradient-to-br from-[#FA8C00] to-[#FA8C00]/80 rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg group-hover:shadow-xl group-hover:scale-110 transition-all duration-300">
                    <span className="text-white text-sm font-bold">✓</span>
                  </div>
                  <span className="text-gray-700 text-lg font-medium group-hover:text-gray-900 transition-colors">
                    {item.text}
                  </span>
                </motion.div>
              ))}
            </div>

          </motion.div>
        </div>

        {/* Right side - Enhanced Auth forms */}
        <div className="flex-1 lg:flex-none lg:w-[520px] flex items-center justify-center p-4 sm:p-8 relative">
          <div className="w-full max-w-md">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3, ease: [0.6, -0.05, 0.01, 0.99] }}
              className={cn(
                "backdrop-blur-xl bg-white/80 border border-white/20 rounded-3xl shadow-2xl",
                isMobile ? "p-6 mx-2" : "p-8"
              )}
            >
              {/* Hivetivity Logo */}
              <div className="flex justify-center mb-6">
                <img 
                  src="https://imagedelivery.net/ylmXutM6_iJT3CT9AqBdjQ/15521506-bf83-4c94-881f-2ed325767800/public" 
                  alt="Hivetivity" 
                  className={cn(
                    "object-contain",
                    isMobile ? "h-10 w-auto" : "h-12 w-auto"
                  )}
                />
              </div>
              
              <Tabs defaultValue={defaultTab} className="w-full">
                <TabsList className={cn(
                  "grid w-full grid-cols-2 bg-gray-100/80 backdrop-blur-sm rounded-2xl p-1 border-0",
                  isMobile ? "mb-6 h-12" : "mb-8 h-11"
                )}>
                  <TabsTrigger 
                    value="signin" 
                    className={cn(
                      "font-medium data-[state=active]:bg-white data-[state=active]:shadow-lg data-[state=active]:text-gray-900 rounded-xl transition-all duration-300",
                      isMobile ? "text-base h-10 min-h-[44px]" : "text-base h-9"
                    )}
                  >
                    Sign In
                  </TabsTrigger>
                  <TabsTrigger 
                    value="signup" 
                    className={cn(
                      "font-medium data-[state=active]:bg-white data-[state=active]:shadow-lg data-[state=active]:text-gray-900 rounded-xl transition-all duration-300",
                      isMobile ? "text-base h-10 min-h-[44px]" : "text-base h-9"
                    )}
                  >
                    Sign Up
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="signin">
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4 }}
                  >
                    <div className="text-center mb-8">
                      <h2 className="text-3xl font-bold text-gray-900 mb-2">Welcome back</h2>
                      <p className="text-gray-600">Sign in to your account</p>
                    </div>
                    <div className="space-y-6">
                      <SignInForm
                        email={email}
                        setEmail={setEmail}
                        password={password}
                        setPassword={setPassword}
                        onSubmit={handleSignIn}
                        onResetPassword={() => setResetMode(true)}
                        loading={loading}
                      />
                    </div>
                  </motion.div>
                </TabsContent>

                <TabsContent value="signup">
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4 }}
                    className="text-center"
                  >
                    <div className="mb-8">
                      <h2 className="text-3xl font-bold text-gray-900 mb-4">Ready to Get Started?</h2>
                      <p className="text-gray-600 text-lg leading-relaxed">Choose your plan and start your 14-day free trial</p>
                    </div>
                    
                    <motion.a 
                      href="/pricing" 
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className={cn(
                        "inline-block bg-gradient-to-r from-[#FA8C00] to-[#FA8C00]/90 text-white rounded-xl font-semibold shadow-xl hover:shadow-2xl transition-all duration-300",
                        isMobile ? "px-6 py-3 text-base min-h-[44px] flex items-center" : "px-8 py-4 text-lg"
                      )}
                    >
                      <span className="flex items-center gap-2">
                        View Pricing Plans
                        <motion.span
                          animate={{ x: [0, 4, 0] }}
                          transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
                        >
                          →
                        </motion.span>
                      </span>
                    </motion.a>
                    
                    <p className="text-sm text-gray-500 mt-4">
                      14-day trial • No credit card required
                    </p>
                  </motion.div>
                </TabsContent>
              </Tabs>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Auth;
